import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'


type CardData = {
  nickname: string
  gender: string
  mbti: string | null
  blood_type: string | null
  day_gan: string
  day_ji: string
  element: string
  card_type_name: string | null
  card_type_desc: string | null
  card_image_path: string | null
  element_wood: number
  element_fire: number
  element_earth: number
  element_metal: number
  element_water: number
}

const ELEMENT_EMOJI: Record<string, string> = {
  목: '🌿', 화: '🔥', 토: '🏔️', 금: '⚙️', 수: '💧',
}

const ELEMENT_COLOR: Record<string, string> = {
  목: 'from-green-900/60 to-green-800/30 border-green-500/30',
  화: 'from-red-900/60 to-orange-800/30 border-red-500/30',
  토: 'from-yellow-900/60 to-amber-800/30 border-amber-500/30',
  금: 'from-slate-700/60 to-slate-600/30 border-slate-400/30',
  수: 'from-blue-900/60 to-blue-800/30 border-blue-500/30',
}

function ElementBar({ label, value, color }: { label: string; value: number; color: string }) {
  const pct = Math.round((value / 8) * 100)
  return (
    <div className="flex items-center gap-2">
      <span className="text-[var(--color-text-tertiary)] text-xs w-6">{label}</span>
      <div className="flex-1 bg-[var(--color-border-default)] rounded-full h-1.5">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-[var(--color-text-disabled)] text-xs w-3">{value}</span>
    </div>
  )
}

export default function CardShare() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [card, setCard] = useState<CardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!token) { setError(true); setLoading(false); return }
    axios.get(`/api/card/${token}`)
      .then((r) => setCard(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [token])

  // 동적 OG 태그 업데이트
  useEffect(() => {
    if (!card) return
    const title = `${card.nickname}님의 사주 캐릭터 카드 | 운명편의점`
    const desc = card.card_type_name
      ? `${card.card_type_name} — ${card.card_type_desc || '사주 × MBTI × 혈액형 캐릭터 카드'}`
      : '사주 × MBTI × 혈액형 캐릭터 카드'
    document.title = title
    const setMeta = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`)
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el) }
      el.setAttribute('content', content)
    }
    setMeta('og:title', title)
    setMeta('og:description', desc)
    setMeta('og:url', window.location.href)
    const imageUrl = card.card_image_path
      ? `${window.location.origin}${card.card_image_path}`
      : `${window.location.origin}/static/og_default.png`
    setMeta('og:image', imageUrl)
    setMeta('twitter:card', 'summary_large_image')
    setMeta('twitter:image', imageUrl)
    return () => { document.title = '운명편의점' }
  }, [card])

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error || !card) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-4xl">🔮</p>
        <h1 className="text-[var(--color-text-primary)] text-xl font-bold">카드를 찾을 수 없어요</h1>
        <p className="text-[var(--color-text-tertiary)] text-sm">링크가 만료되었거나 잘못된 주소예요.</p>
        <button onClick={() => navigate('/')}
          className="mt-2 px-6 py-2.5 bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] rounded-xl font-bold text-sm transition-opacity">
          홈으로 가기
        </button>
      </div>
    )
  }

  const bgGradient = ELEMENT_COLOR[card.element] || ELEMENT_COLOR['토']

  return (
    <div className="min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-4">
        {/* 헤더 */}
        <div className="text-center">
          <p className="text-[var(--color-text-tertiary)] text-xs mb-1">🏮 사주 × MBTI × 혈액형 캐릭터 카드</p>
          <h1 className="text-[var(--color-text-primary)] text-2xl font-bold">{card.nickname}님의 카드</h1>
        </div>

        {/* 캐릭터 카드 */}
        <div className={`bg-gradient-to-b ${bgGradient} border rounded-2xl p-6 text-center space-y-4`}>
          {card.card_image_path ? (
            <div className="rounded-2xl overflow-hidden aspect-[3/4] w-48 mx-auto">
              <img src={card.card_image_path} alt="캐릭터 카드" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="text-7xl">{ELEMENT_EMOJI[card.element] || '🌟'}</div>
          )}

          <div>
            <p className="text-[var(--color-text-tertiary)] text-xs mb-1">{card.day_gan}일간 · {card.element}의 기운</p>
            <h2 className="text-[var(--color-text-primary)] text-xl font-bold">
              {card.card_type_name || `${card.day_gan}의 존재`}
            </h2>
            {card.card_type_desc && (
              <p className="text-[var(--color-text-secondary)] text-sm mt-2 leading-relaxed">{card.card_type_desc}</p>
            )}
          </div>

          <div className="flex justify-center gap-2 flex-wrap">
            {card.mbti && (
              <span className="px-3 py-1 bg-[var(--color-accent)]/20 border border-[var(--color-accent)]/30 text-[var(--color-accent)] rounded-full text-xs font-medium">
                {card.mbti}
              </span>
            )}
            {card.blood_type && (
              <span className="px-3 py-1 bg-[var(--color-border-default)]/30 border border-[var(--color-border-default)] text-[var(--color-text-secondary)] rounded-full text-xs font-medium">
                {card.blood_type}형
              </span>
            )}
            <span className="px-3 py-1 bg-[var(--color-border-default)]/30 border border-[var(--color-border-default)] text-[var(--color-text-secondary)] rounded-full text-xs font-medium">
              {card.gender === 'M' ? '남성' : '여성'}
            </span>
          </div>

          {/* 오행 분포 */}
          <div className="bg-black/20 rounded-xl p-4 space-y-2 text-left">
            <p className="text-xs text-[var(--color-text-tertiary)] mb-3">오행 분포</p>
            <ElementBar label="목" value={card.element_wood} color="bg-green-500" />
            <ElementBar label="화" value={card.element_fire} color="bg-red-500" />
            <ElementBar label="토" value={card.element_earth} color="bg-yellow-600" />
            <ElementBar label="금" value={card.element_metal} color="bg-gray-400" />
            <ElementBar label="수" value={card.element_water} color="bg-blue-500" />
          </div>
        </div>

        {/* 가입 유도 CTA */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-accent)]/20 rounded-2xl p-5 text-center space-y-3">
          <p className="text-[var(--color-accent)] text-sm font-medium">✨ 나의 사주 캐릭터 카드는?</p>
          <p className="text-[var(--color-text-tertiary)] text-xs leading-relaxed">
            사주 × MBTI × 혈액형을 조합한 나만의 캐릭터 카드를 확인하고<br />
            AI 사주 상담도 받아보세요!
          </p>
          <button onClick={() => navigate('/register')}
            className="w-full py-3 bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] rounded-xl font-bold text-sm transition-opacity">
            무료로 내 카드 만들기
          </button>
          <button onClick={() => navigate('/login')}
            className="w-full py-2 text-[var(--color-text-disabled)] hover:text-[var(--color-text-secondary)] text-sm transition-colors">
            이미 회원이에요 →
          </button>
        </div>
      </div>
    </div>
  )
}
